'use client';

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { useSession, signIn } from 'next-auth/react';
import { PiArrowUpRightBold, PiListLight, PiX } from 'react-icons/pi';
import { ButtonShadcn } from '@/components/ui/button-shadcn';
import { IconButton } from '@/components/ui/button';
import { HeaderLogo } from './header-logo';
import { MenuLink } from './menu-link';
import { MobileUserButton } from './mobile-user-button';
import { Separator } from '@/components/ui/separator';

type MobileMenuLabels = {
  home: string;
  practice: string;
  posts: string;
  about: string;
  startLearning: string;
  login?: string;
  manageAccount?: string;
  myProgress?: string;
  signOut?: string;
};

type MobileMenuProps = {
  homeHref: string;
  practiceHref: string;
  postsHref: string;
  aboutHref: string;
  labels: MobileMenuLabels;
};

export const MobileMenu = ({
  homeHref,
  practiceHref,
  postsHref,
  aboutHref,
  labels,
}: MobileMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: session, status } = useSession();
  const isLoading = status === 'loading';
  const isLoggedIn = !!session?.user;

  return (
    <>
      <div className='flex lg:hidden flex-row gap-4 items-center'>
        <MobileUserButton
          loginAriaLabel={labels.login}
          manageAccountLabel={labels.manageAccount}
          myProgressLabel={labels.myProgress}
          signOutLabel={labels.signOut}
        />
        {/* Mobile/Tablet Burger Button */}
        <IconButton
          onClick={() => setIsOpen(true)}
          aria-label='Open menu'
          className='lg:hidden'
        >
          <PiListLight size={28} />
        </IconButton>
      </div>

      {/* Mobile/Tablet Full Screen Menu */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div className='lg:hidden fixed inset-0 z-50 bg-white flex flex-col' style={{ backgroundColor: 'white', opacity: 1 }}>
          {/* Header */}
          <div className='flex items-center justify-between py-3 px-6 border-b'>
            <HeaderLogo href={homeHref} />
            <IconButton
              onClick={() => setIsOpen(false)}
              aria-label='Close menu'
              className='lg:hidden'
            >
              <PiX size={28} />
            </IconButton>
          </div>

          {/* Menu Content */}
          <div className='flex-1 flex flex-col items-start justify-start gap-6 p-8'>
            <div className='flex flex-col items-start justify-between gap-8'>
            <MenuLink
              href={homeHref}
              label={labels.home}
              variant='mobile'
              onClick={() => setIsOpen(false)}
            />
            <MenuLink
              href={practiceHref}
              label={labels.practice}
              variant='mobile'
              onClick={() => setIsOpen(false)}
            />
            <MenuLink
              href={postsHref}
              label={labels.posts}
              variant='mobile'
              onClick={() => setIsOpen(false)}
            />
            <MenuLink
              href={aboutHref}
              label={labels.about}
              variant='mobile'
              onClick={() => setIsOpen(false)}
            />
            </div>
            <Separator/>
            {!isLoading && !isLoggedIn && (
              <div className='w-full flex flex-col md:flex-row gap-3'>
                <ButtonShadcn asChild variant='blue' className='w-full'>
                  <Link
                    href={practiceHref}
                    onClick={() => setIsOpen(false)}
                    className='flex items-center justify-center gap-2'
                  >
                    {labels.startLearning}
                    <PiArrowUpRightBold />
                  </Link>
                </ButtonShadcn>
                <ButtonShadcn
                  variant='outline'
                  className='w-full'
                  onClick={() => {
                    setIsOpen(false);
                    signIn();
                  }}
                >
                  {labels.login || 'Login'}
                </ButtonShadcn>
              </div>
            )}
          </div>

          {/* Tablet Start Learning Button */}
          {/* <div className='hidden md:flex lg:hidden'>
            <ButtonShadcn asChild variant='blue' size='sm'>
              <Link href={practiceHref} className='flex items-center gap-1'>
                {labels.startLearning}
                <PiArrowUpRightBold />
              </Link>
            </ButtonShadcn>
          </div> */}
        </div>,
        document.body
      )}
    </>
  );
};
